<script setup lang="ts">
import { ref } from 'vue'
import { RouterLink } from 'vue-router'
import { useCookieConsent } from '@/composables/useCookieConsent'

const { hasConsented, preferences, acceptAll, rejectNonEssential, saveCustom } = useCookieConsent()

const showCustomize = ref(false)
const customAnalytics = ref(preferences.value.analytics)
const customMarketing = ref(preferences.value.marketing)

function handleAcceptAll() {
  acceptAll()
}

function handleReject() {
  rejectNonEssential()
}

function handleSaveCustom() {
  saveCustom({
    analytics: customAnalytics.value,
    marketing: customMarketing.value
  })
}
</script>

<template>
  <Transition name="cookie-banner">
    <div
      v-if="!hasConsented"
      class="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
    >
      <div class="max-w-2xl mx-auto bg-white shadow-elevated rounded-3xl p-6 md:p-8 border border-neutral-100">
        <!-- Main content -->
        <div v-if="!showCustomize">
          <div class="flex items-start gap-4 mb-5">
            <div class="w-10 h-10 rounded-2xl bg-amber-50 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg class="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
            </div>
            <div>
              <h3 class="text-base font-semibold text-neutral-900 mb-1">Respect de votre vie privée</h3>
              <p class="text-sm text-neutral-600 leading-relaxed">
                Nous utilisons des cookies pour améliorer votre expérience et analyser l'utilisation du site.
                <RouterLink to="/legal/cookies" class="text-[#C9A96E] hover:underline">En savoir plus</RouterLink>
              </p>
            </div>
          </div>

          <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              @click="handleAcceptAll"
              class="flex-1 px-5 py-2.5 bg-[#C9A96E] text-white text-sm font-semibold rounded-xl hover:bg-[#b8935d] transition-colors"
            >
              Accepter tout
            </button>
            <button
              @click="handleReject"
              class="flex-1 px-5 py-2.5 bg-neutral-100 text-neutral-700 text-sm font-semibold rounded-xl hover:bg-neutral-200 transition-colors"
            >
              Refuser non-essentiels
            </button>
            <button
              @click="showCustomize = true"
              class="px-5 py-2.5 text-neutral-500 text-sm font-medium hover:text-neutral-700 transition-colors"
            >
              Personnaliser
            </button>
          </div>
        </div>

        <!-- Customization panel -->
        <div v-else>
          <h3 class="text-base font-semibold text-neutral-900 mb-4">Paramètres des cookies</h3>

          <div class="space-y-4 mb-6">
            <!-- Essential (always on) -->
            <div class="flex items-center justify-between py-3 border-b border-neutral-100">
              <div>
                <p class="text-sm font-medium text-neutral-900">Essentiels</p>
                <p class="text-xs text-neutral-500 mt-0.5">Nécessaires au fonctionnement du site</p>
              </div>
              <div class="w-10 h-6 bg-[#C9A96E] rounded-full flex items-center justify-end px-0.5 opacity-60 cursor-not-allowed">
                <div class="w-5 h-5 bg-white rounded-full shadow-sm"></div>
              </div>
            </div>

            <!-- Analytics -->
            <label class="flex items-center justify-between py-3 border-b border-neutral-100 cursor-pointer">
              <div>
                <p class="text-sm font-medium text-neutral-900">Analytiques</p>
                <p class="text-xs text-neutral-500 mt-0.5">Nous aident à comprendre l'utilisation du site</p>
              </div>
              <button
                @click="customAnalytics = !customAnalytics"
                :class="[
                  'w-10 h-6 rounded-full flex items-center px-0.5 transition-colors',
                  customAnalytics ? 'bg-[#C9A96E] justify-end' : 'bg-neutral-200 justify-start'
                ]"
              >
                <div class="w-5 h-5 bg-white rounded-full shadow-sm"></div>
              </button>
            </label>

            <!-- Marketing -->
            <label class="flex items-center justify-between py-3 cursor-pointer">
              <div>
                <p class="text-sm font-medium text-neutral-900">Marketing</p>
                <p class="text-xs text-neutral-500 mt-0.5">Permettent de personnaliser les publicités</p>
              </div>
              <button
                @click="customMarketing = !customMarketing"
                :class="[
                  'w-10 h-6 rounded-full flex items-center px-0.5 transition-colors',
                  customMarketing ? 'bg-[#C9A96E] justify-end' : 'bg-neutral-200 justify-start'
                ]"
              >
                <div class="w-5 h-5 bg-white rounded-full shadow-sm"></div>
              </button>
            </label>
          </div>

          <div class="flex items-center gap-3">
            <button
              @click="handleSaveCustom"
              class="flex-1 px-5 py-2.5 bg-[#C9A96E] text-white text-sm font-semibold rounded-xl hover:bg-[#b8935d] transition-colors"
            >
              Enregistrer mes préférences
            </button>
            <button
              @click="showCustomize = false"
              class="px-5 py-2.5 text-neutral-500 text-sm font-medium hover:text-neutral-700 transition-colors"
            >
              Retour
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.cookie-banner-enter-active {
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease;
}
.cookie-banner-leave-active {
  transition: transform 0.3s cubic-bezier(0.7, 0, 0.84, 0), opacity 0.2s ease;
}
.cookie-banner-enter-from {
  transform: translateY(100%);
  opacity: 0;
}
.cookie-banner-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
